import { Select } from "antd";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
  IWorkflowsSurveyOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import { useWardLabel } from "../../../../../../../hooks/useWardLabel";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  SelectContainer,
  TextFieldCss,
  popupOptionsCss,
} from "../RuleComponent.styles";

interface ISurveyScoreFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  surveyScoreConditionComparators?: IWorkflowsSelectOption[];
  surveyOptions?: IWorkflowsSurveyOption[];
}

export function SurveyScoreFields({
  formik,
  surveyScoreConditionComparators,
  surveyOptions,
}: ISurveyScoreFieldsProps) {
  const { t } = useTranslation();

  const wardLabel = useWardLabel();

  const shouldShowErrorMessage = (
    field: `conditions[${number}].${keyof IRuleCondition}`,
    index?: number
  ): string => {
    if (field.startsWith("conditions")) {
      const key = field.split(".")[1] as keyof IRuleCondition;

      const touchedArray = formik.touched.conditions as
        | FormikTouched<IRuleCondition>[]
        | undefined;
      const errorArray = formik.errors.conditions as
        | FormikErrors<IRuleCondition>[]
        | undefined;

      if (touchedArray && errorArray) {
        const touched = touchedArray[index ?? 0];
        const error = errorArray[index ?? 0];

        return touched?.[key] && error?.[key] ? (error[key] as string) : "";
      }

      return "";
    }

    return "";
  };

  const surveyOptionDropdown = useMemo(() => {
    const optionsToDisplay: IOption[] =
      surveyOptions
        ?.filter((s) => s.surveyDomains.length > 0)
        .map((surveyOption) => {
          return {
            label: surveyOption.surveyDisplayName,
            value: surveyOption.surveyId,
            key: surveyOption.surveyId,
          };
        }) ?? [];

    return (
      <Select
        placeholder={t("workflows_rule_survey")}
        value={formik.values.conditions[0].surveyId}
        suffixIcon={<ArrowDown />}
        options={optionsToDisplay}
        popupClassName={popupOptionsCss()}
        notFoundContent={t("workflows_rule_noDataForWards", {
          wards: wardLabel.pluralLower,
        })}
        className={`${selectCss()} nopan nodrag`}
        onChange={(value) => {
          formik.setFieldValue("conditions[0].surveyId", value);
          formik.setFieldValue("conditions[0].surveyDomain", undefined);
          formik.setFieldValue(
            "conditions[0].conditionComparatorType",
            undefined
          );
          formik.setFieldValue("conditions[0].threshold", undefined);
        }}
      />
    );
  }, [surveyOptions, formik.values.conditions[0].surveyId]);

  const surveyDomainsDropdown = useMemo(() => {
    return (
      <Select
        placeholder={t("workflows_rule_totalDomain")}
        suffixIcon={<ArrowDown />}
        notFoundContent={t("workflows_rule_noData")}
        value={formik.values.conditions[0].surveyDomain}
        options={
          surveyOptions
            ?.find((s) => s.surveyId === formik.values.conditions[0].surveyId)
            ?.surveyDomains.map((surveyDomain) => {
              return {
                label: surveyDomain,
                value: surveyDomain,
                key: uuidv4(),
              };
            }) ?? []
        }
        onChange={(value) => {
          formik.setFieldValue("conditions[0].surveyDomain", value);
        }}
        className={`${selectCss()} nopan nodrag`}
        popupClassName={popupOptionsCss()}
        maxTagTextLength={24}
        maxTagCount={1}
      />
    );
  }, [
    surveyOptions,
    formik.values.conditions[0].surveyId,
    formik.values.conditions[0].surveyDomain,
  ]);

  const handleSurveyScoreThresholdName = () => {
    if (
      formik.values.conditions[0].conditionComparatorType ===
        "IncreaseSinceLastScore" ||
      formik.values.conditions[0].conditionComparatorType ===
        "DecreaseSinceLastScore"
    ) {
      return t("workflows_rule_change");
    }

    return t("workflows_rule_value");
  };

  return (
    <>
      <SelectContainer
        isError={shouldShowErrorMessage("conditions[0].surveyId") !== ""}
      >
        {surveyOptionDropdown}
        <ErrorMessage>
          {shouldShowErrorMessage("conditions[0].surveyId")}
        </ErrorMessage>
      </SelectContainer>

      {formik.values.conditions[0].surveyId !== undefined ? (
        <SelectContainer
          isError={shouldShowErrorMessage("conditions[0].surveyDomain") !== ""}
        >
          <p>{t("workflows_rule_totalDomain")}</p>
          {surveyDomainsDropdown}
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].surveyDomain")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.conditions[0].surveyDomain !== undefined ? (
        <SelectContainer
          isError={
            shouldShowErrorMessage(
              "conditions[0].conditionComparatorType",
              0
            ) !== ""
          }
        >
          <p>{t("workflows_rule_comparator")}</p>
          <SelectDropdown
            placeholder={t("workflows_rule_greaterThan")}
            value={formik.values.conditions[0].conditionComparatorType}
            options={
              surveyScoreConditionComparators?.map((ruleComparator) => {
                return {
                  label: getWorkflowOptionLabel(
                    "comparator",
                    ruleComparator,
                    t
                  ),
                  value: ruleComparator.type,
                  key: ruleComparator.type,
                };
              }) ?? []
            }
            onValueChange={(value) =>
              formik.setFieldValue(
                "conditions[0].conditionComparatorType",
                value as string
              )
            }
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].conditionComparatorType", 0)}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.conditions[0].conditionComparatorType !== undefined ? (
        <SelectContainer
          isError={shouldShowErrorMessage("conditions[0].threshold", 0) !== ""}
        >
          <p>{handleSurveyScoreThresholdName()}</p>
          <TextField
            name="conditions[0].threshold"
            placeholder="0"
            value={formik.values.conditions[0].threshold}
            label=""
            onChange={formik.handleChange}
            backgroudColor="white"
            className={`${TextFieldCss()} nopan nodrag`}
            type="number"
          />
          <ErrorMessage>
            {shouldShowErrorMessage("conditions[0].threshold", 0)}
          </ErrorMessage>
        </SelectContainer>
      ) : null}
    </>
  );
}
